import { useEffect } from "react";

import {
  EntityTypesSupportingSavedFilters,
  MapFilterEntityType,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import MapFilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import useMapView from "@app/component/view/utils/useMapView";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

// Apply rules from saved filter when it is changed
const useSavedMapFilterApplicator = <T extends MapSavedFilter>(
  entityTypes: MapFilterEntityType[],
  entityType: EntityTypesSupportingSavedFilters,
  selectedFilter: T | undefined,
  implicitFilterFields: IField[] | undefined,
  processSavedFilter?: (savedFilter: T) => T | undefined,
  onFilterApplied?: (before: MapFilterModel, after: MapFilterModel) => void
) => {
  const { updateFilterModel, updateViewAs, viewState } = useMapView();
  const { filter: currentFilters } = viewState;

  useEffect(
    () => {
      if (!selectedFilter || selectedFilter.type !== entityType) {
        return;
      }

      const processedFilter = processSavedFilter
        ? processSavedFilter(selectedFilter)
        : selectedFilter;
      if (!processedFilter) {
        return;
      }

      const implicitFieldFilterNames = new Set(
        (implicitFilterFields ?? []).map(({ filterName }) => filterName)
      );

      const newFilters = entityTypes.reduce((result, entityType) => {
        const fieldModel = getFieldModelByEntityType(entityType);
        let processedFilters = fieldModel.fields.reduce<Partial<FilterModel>>(
          (result, field) =>
            Object.assign(result, {
              [field.filterName]: implicitFieldFilterNames.has(field.filterName)
                ? currentFilters[entityType]?.[field.filterName]
                : processedFilter.filters[entityType]?.[field.filterName],
            }),
          {}
        );
        // if all are fields are undefined, mark whole entityType as empty
        if (Object.keys(processedFilters).every((fieldName) => !processedFilters[fieldName])) {
          processedFilters = {};
        }
        return Object.assign(result, {
          [entityType]: Object.keys(processedFilters).length ? processedFilters : undefined,
        });
      }, {});

      onFilterApplied?.(currentFilters, newFilters);
      updateFilterModel(
        // we need to provide values for all filters to ensure there are no traces left after the
        // previously applied filter, except implicitly filtered fields. Take implicit fields'
        // filters from filter var instead.
        newFilters
      );
      updateViewAs(processedFilter.viewAs);
    },
    // we skipped "filter" var here because otherwise it will call useEffect infinitely
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      entityType,
      implicitFilterFields,
      processSavedFilter,
      updateFilterModel,
      updateViewAs,
      selectedFilter,
    ]
  );
};

export default useSavedMapFilterApplicator;
